import React, {useState, useEffect, useRef} from 'react';
import axios from 'axios';
import './References.css';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL;

const References = ({onNavigate}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtreType, setFiltreType] = useState('');
  const [selectedReference, setSelectedReference] = useState(null);
  const [globalStats, setGlobalStats] = useState({totalClients: 0, totalPuissance: 0});
  const [chartAnimated, setChartAnimated] = useState(false);
  const chartRef = useRef(null);

  useEffect(() => {
    fetchReferences();
    fetchGlobalStats();
  }, [filtreType]);

  const fetchGlobalStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/references/stats/global`);
      setGlobalStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques globales:', error);
    }
  };

  const fetchReferences = async () => {
    try {
      setLoading(true);
      const queryParams = filtreType ? `?typeReference=${filtreType}` : '';
      const response = await axios.get(`${API_URL}/references${queryParams}`);
      setReferences(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des références:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      'MT': '#1e3a5f',        // Bleu primaire SASTRA
      'BT': '#ffd700',        // Jaune primaire SASTRA
      'Pompage': '#ff8c00',   // Orange accent SASTRA
      'Isolé': '#ffb700'      // Jaune secondaire SASTRA
    };
    return colors[type] || '#6c757d';
  };

  const countByType = (type) => {
    return references.filter(ref => ref.typeReference === type).length;
  };

  const openFullscreen = () => {
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    document.body.style.overflow = 'auto';
  };

  const openReferenceDetail = (reference) => {
    setSelectedReference(reference);
  };

  const closeReferenceDetail = () => {
    setSelectedReference(null);
  };

  // Fermer avec la touche Escape
  React.useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (isFullscreen) {
          closeFullscreen();
        }
        if (selectedReference) {
          closeReferenceDetail();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isFullscreen, selectedReference]);

  const yearlyData = [
    {year: 2016, power: 50},
    {year: 2017, power: 180},
    {year: 2018, power: 500},
    {year: 2019, power: 1250},
    {year: 2020, power: 1500},
    {year: 2021, power: 2150},
    {year: 2022, power: 3200},
    {year: 2023, power: 4000}
  ];

  // Observer pour animation du graphique
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !chartAnimated) {
            setChartAnimated(true);
          }
        });
      },
      {threshold: 0.3}
    );

    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => {
      if (chartRef.current) {
        observer.unobserve(chartRef.current);
      }
    };
  }, [chartAnimated]);

  return (
    <div className="references" id="references">
      {/* Hero Section */}
      <section className="references-hero">
        <div className="container">
          <div className="references-hero-content">
            <br />
            <h1 className="references-title">Nos Références</h1>
            <p className="references-subtitle">
              Découvrez nos réalisations et projets en cours depuis 2016
            </p>
          </div>
        </div>
      </section>

      {/* Nos Références Clients */}
      <section className="clients-references-section">
        <div className="container">
          <div className="section-header">
            <h2>Nos Clients & Partenaires</h2>
            <p>Découvrez les entreprises qui nous font confiance</p>
          </div>

          {/* Filtres */}
          <div className="references-filters">
            <label>Filtrer par type :</label>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${filtreType === '' ? 'active' : ''}`}
                onClick={() => setFiltreType('')}
              >
                Tous
              </button>
              {['MT', 'BT', 'Pompage', 'Isolé'].map(type => (
                <button
                  key={type}
                  className={`filter-btn ${filtreType === type ? 'active' : ''}`}
                  onClick={() => setFiltreType(type)}
                  style={{
                    backgroundColor: filtreType === type ? getTypeColor(type) : 'transparent',
                    borderColor: getTypeColor(type),
                    color: filtreType === type ? 'white' : getTypeColor(type)
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Grille des références */}
          {loading ? (
            <LoadingSpinner message="Chargement des références..." />
          ) : references.length === 0 ? (
            <div className="empty-state">
              <p>Aucune référence disponible</p>
              <small>Revenez plus tard pour découvrir nos réalisations</small>
            </div>
          ) : (
            <div className="references-carousel">
              <div className="references-track">
                {/* Premier groupe de références */}
                {references.map((ref) => (
                  <div
                    key={ref._id}
                    className="reference-slide"
                    onClick={() => openReferenceDetail(ref)}
                  >
                    <div className="ref-logo-container">
                      <img
                        src={`${API_URL.replace('/api', '')}${ref.logo}`}
                        alt={ref.nom}
                        className="ref-logo-img"
                      />
                      <div
                        className="ref-type-badge"
                        style={{backgroundColor: getTypeColor(ref.typeReference)}}
                      >
                        {ref.typeReference}
                      </div>
                    </div>
                    <div className="ref-info-container">
                      <h3>{ref.nom}</h3>
                      <div className="ref-power">
                        <span className="power-icon">⚡</span>
                        <span className="power-value">{ref.puissance} </span>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Duplication pour animation continue */}
                {references.map((ref) => (
                  <div
                    key={`duplicate-${ref._id}`}
                    className="reference-slide"
                    onClick={() => openReferenceDetail(ref)}
                  >
                    <div className="ref-logo-container">
                      <img
                        src={`${API_URL.replace('/api', '')}${ref.logo}`}
                        alt={ref.nom}
                        className="ref-logo-img"
                      />
                      <div
                        className="ref-type-badge"
                        style={{backgroundColor: getTypeColor(ref.typeReference)}}
                      >
                        {ref.typeReference}
                      </div>
                    </div>
                    <div className="ref-info-container">
                      <h3>{ref.nom}</h3>
                      <div className="ref-power">
                        <span className="power-icon">⚡</span>
                        <span className="power-value">{ref.puissance} </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Statistiques Globales */}
      <section className="global-stats-section">
        <div className="container">
          <div className="stats-header-modern">
            <h2 className="stats-title-modern">Notre Impact en Chiffres</h2>
            <p className="stats-subtitle-modern">Des résultats qui parlent d'eux-mêmes</p>
          </div>
          <div className="global-stats-cards">
            {/* Card 1 : Clients BT */}
            <div className="global-stat-card">
              <div className="stat-card-glow"></div>
              <div className="stat-content-wrapper">
                <div className="global-stat-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13M16 3.13C16.8604 3.3503 17.623 3.8507 18.1676 4.55231C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="global-stat-value">
                  <span className="stat-number">{globalStats.clientsBT || globalStats.totalClients || 0}</span>
                  <span className="stat-plus">+</span>
                </div>
                <div className="global-stat-label">Clients BT</div>
                <div className="stat-description">Qui nous font confiance</div>
              </div>
            </div>

            {/* Card 2 : Puissance Totale Installée */}
            <div className="global-stat-card">
              <div className="stat-card-glow"></div>
              <div className="stat-content-wrapper">
                <div className="global-stat-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="global-stat-value">
                  <span className="stat-number">{Number(globalStats.totalPuissance || globalStats.puissanceInstallee || 0).toFixed(2)}</span>
                  <span className="stat-unit">kWc</span>
                </div>
                <div className="global-stat-label">Puissance Installée</div>
                <div className="stat-description">Énergie solaire globale</div>
              </div>
            </div>

            {/* Card 3 : Puissance BT (Basse Tension) */}
            {globalStats.puissanceBT > 0 && (
              <div className="global-stat-card">
                <div className="stat-card-glow"></div>
                <div className="stat-content-wrapper">
                  <div className="global-stat-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 9.5L12 3L21 9.5V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V9.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M13 10L10 14H14L11 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="global-stat-value">
                    <span className="stat-number">{Number(globalStats.puissanceBT || 0).toFixed(2)}</span>
                    <span className="stat-unit">kWc</span>
                  </div>
                  <div className="global-stat-label">Puissance BT</div>
                  <div className="stat-description">Basse Tension</div>
                </div>
              </div>
            )}

            {/* Card 4 : Puissance MT (Moyenne Tension) */}
            {globalStats.puissanceMT > 0 && (
              <div className="global-stat-card">
                <div className="stat-card-glow"></div>
                <div className="stat-content-wrapper">
                  <div className="global-stat-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 21H22M3 21V10L9 13V8L15 11V3L21 6V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M17 10L15 13H18L16 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="global-stat-value">
                    <span className="stat-number">{Number(globalStats.puissanceMT || 0).toFixed(2)}</span>
                    <span className="stat-unit">kWc</span>
                  </div>
                  <div className="global-stat-label">Puissance MT</div>
                  <div className="stat-description">Moyenne Tension</div>
                </div>
              </div>
            )}

            {/* Card 5 : Pompage Solaire */}
            {globalStats.puissancePompage > 0 && (
              <div className="global-stat-card">
                <div className="stat-card-glow"></div>
                <div className="stat-content-wrapper">
                  <div className="global-stat-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2.69L5.64 10.35C3.45 12.98 3.86 16.89 6.55 19.01C9.24 21.13 13.11 20.75 15.3 18.12C17.49 15.49 17.08 11.58 14.39 9.46L12 2.69Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 11L10 14H14L12 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="global-stat-value">
                    <span className="stat-number">{Number(globalStats.puissancePompage || 0).toFixed(2)}</span>
                    <span className="stat-unit">kWc</span>
                  </div>
                  <div className="global-stat-label">Pompage Solaire</div>
                  <div className="stat-description">Irrigation & Agricole</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Galerie de Réalisations */}
      <section className="realisations-gallery-section">
        <div className="container">
          <div className="section-header">
            <h2>Nos Réalisations</h2>
            <p>Découvrez quelques-unes de nos installations photovoltaïques</p>
          </div>

          <div className="realisations-intro">
            <h3>Excellence et Qualité dans Chaque Installation</h3>
            <p>
              Depuis plus de 10 ans, SASTRA Energy transforme l'énergie solaire en solutions concrètes pour nos clients.
              Chaque projet est le fruit d'une expertise technique pointue et d'un engagement sans faille pour la qualité.
              Découvrez ci-dessous quelques exemples de nos installations photovoltaïques réalisées avec succès.
            </p>
          </div>

          <div className="realisations-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <div key={num} className="realisation-card">
                <div className="realisation-image-wrapper">
                  <img
                    src={`/références/images/${num}.jpg`}
                    alt={`Réalisation ${num}`}
                    className="realisation-image"
                  />
                  <div className="realisation-overlay">
                    <div className="realisation-overlay-content">
                      <h4>Installation Solaire</h4>
                      <p>Projet réalisé par SASTRA Energy</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Evolution Section */}
      <section className="evolution-section">
        <div className="container">
          <div className="section-header">
            <h2>Notre Croissance</h2>

            <p>
              La société Sastra Solar Energy est active depuis 2016. La puissance totale installée durant
              l'année 2023 s'élève à <strong>4 MWc</strong> comme présenté dans la figure ci-dessous.
            </p>
          </div>

          <div className="evolution-content-large">
            <div className="chart-container-large">
              <div className="chart-title">
                <h3>Figure 2 : Évolution de la puissance totale installée à Sastra entre 2016 et 2023</h3>
              </div>

              <div className="power-chart-large" ref={chartRef}>
                <div className="animated-chart-container">
                  <svg className="animated-bar-chart" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid meet">
                    {/* Flèche de croissance */}
                    <defs>
                      <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                        <polygon points="0 0, 10 3, 0 6" fill="#1e3a5f" />
                      </marker>
                    </defs>

                    {/* Ligne de croissance avec flèche zigzag */}
                    {chartAnimated && (
                      <path
                        className="growth-arrow"
                        d={`M 70 ${350 - (yearlyData[0].power / 4000) * 300}
                            L ${70 + 85} ${350 - (yearlyData[1].power / 4000) * 300}
                            L ${70 + 85 * 2} ${350 - (yearlyData[2].power / 4000) * 300}
                            L ${70 + 85 * 3} ${350 - (yearlyData[3].power / 4000) * 300}
                            L ${70 + 85 * 4} ${350 - (yearlyData[4].power / 4000) * 300}
                            L ${70 + 85 * 5} ${350 - (yearlyData[5].power / 4000) * 300}
                            L ${70 + 85 * 6} ${350 - (yearlyData[6].power / 4000) * 300}
                            L 730 ${350 - (yearlyData[yearlyData.length - 1].power / 4000) * 300}`}
                        stroke="#1e3a5f"
                        strokeWidth="3"
                        fill="none"
                        markerEnd="url(#arrowhead)"
                        strokeDasharray="1000"
                        strokeDashoffset={chartAnimated ? "0" : "1000"}
                      />
                    )}

                    {/* Axes */}
                    <line x1="50" y1="350" x2="750" y2="350" stroke="#ccc" strokeWidth="2" />
                    <line x1="50" y1="50" x2="50" y2="350" stroke="#ccc" strokeWidth="2" />

                    {/* Labels Y-axis */}
                    <text x="30" y="355" fontSize="12" fill="#666">0</text>
                    <text x="20" y="255" fontSize="12" fill="#666">1000</text>
                    <text x="20" y="155" fontSize="12" fill="#666">2000</text>
                    <text x="20" y="80" fontSize="12" fill="#666">3000</text>
                    <text x="20" y="55" fontSize="12" fill="#666">4000</text>
                    <text x="5" y="30" fontSize="13" fill="#1e3a5f" fontWeight="bold">kWc</text>

                    {/* Barres */}
                    {yearlyData.map((data, index) => {
                      const x = 70 + index * 85;
                      const barHeight = (data.power / 4000) * 300;
                      const y = 350 - barHeight;

                      return (
                        <g key={data.year}>
                          {/* Barre principale */}
                          <rect
                            x={x}
                            y={chartAnimated ? y : 350}
                            width="60"
                            height={chartAnimated ? barHeight : 0}
                            fill="#1e3a5f"
                            filter="url(#barShadow)"
                            rx="4"
                            ry="4"
                            className="animated-bar"
                            style={{
                              transition: `height 1.5s ease-out ${index * 0.1}s, y 1.5s ease-out ${index * 0.1}s`
                            }}
                          />

                          {/* Valeur au-dessus */}
                          {(() => {
                            const boxY = y - 45;
                            const boxHeight = 28;

                            return (
                              <>
                                {/* Rectangle blanc */}
                                <rect
                                  x={x + 5}
                                  y={boxY}
                                  width="50"
                                  height={boxHeight}
                                  fill="white"
                                  rx="5"
                                  stroke="#e0e0e0"
                                  strokeWidth="1.5"
                                  style={{
                                    opacity: chartAnimated ? 0.95 : 0,
                                    transition: `opacity 0.5s ease-in ${index * 0.1 + 1.2}s`
                                  }}
                                />

                                {/* Texte centré */}
                                <text
                                  x={x + 30}
                                  y={boxY + boxHeight / 2}
                                  fontSize="16"
                                  fontWeight="bold"
                                  fill="#1e3a5f"
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  style={{
                                    opacity: chartAnimated ? 1 : 0,
                                    transition: `opacity 0.5s ease-in ${index * 0.1 + 1.2}s`
                                  }}
                                >
                                  {data.power}
                                </text>
                              </>
                            );
                          })()}

                          {/* Année */}
                          <text
                            x={x + 30}
                            y="370"
                            fontSize="13"
                            fontWeight="bold"
                            fill="#1e3a5f"
                            textAnchor="middle"
                          >
                            {data.year}
                          </text>
                        </g>

                      );
                    })}

                    {/* Filtres */}
                    <defs>
                      <filter id="barShadow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
                        <feOffset dx="2" dy="4" result="offsetblur" />
                        <feComponentTransfer>
                          <feFuncA type="linear" slope="0.5" />
                        </feComponentTransfer>
                        <feMerge>
                          <feMergeNode />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>
                  </svg>

                  <div className="chart-fullscreen-btn" onClick={openFullscreen} title="Voir en plein écran">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 14H5V19H10V17H7V14Z" fill="currentColor" />
                      <path d="M19 14V17H16V19H21V14H19Z" fill="currentColor" />
                      <path d="M5 10H7V7H10V5H5V10Z" fill="currentColor" />
                      <path d="M17 7H19V10H21V5H16V7H17Z" fill="currentColor" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <br />
          <div className="stats-section">
            <div className="stats-summary">
              <div className="stat-card primary">
                <div className="stat-icon">📈</div>
                <div className="stat-info">
                  <h4>4 MWc</h4>
                  <p>Puissance totale installée en 2023</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">🏢</div>
                <div className="stat-info">
                  <h4>8000%</h4>
                  <p>Croissance depuis 2016</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">⚡</div>
                <div className="stat-info">
                  <h4>8 ans</h4>
                  <p>D'expérience continue</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="references-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Rejoignez Nos Références</h2>
            <p>Faites confiance à notre expertise pour votre projet solaire</p>
            <div className="cta-buttons">
              <button className="btn-primary" onClick={() => onNavigate && onNavigate('devis')}>
                Demander un Devis
              </button>
              <button className="btn-outline" onClick={() => onNavigate && onNavigate('home')}>
                Voir Plus de Projets
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Modal Plein Écran Chart */}
      {isFullscreen && (
        <div className="fullscreen-modal" onClick={closeFullscreen}>
          <div className="fullscreen-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-fullscreen-btn" onClick={closeFullscreen} title="Fermer (Échap)">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="fullscreen-chart-title">
              <h3>Figure 2 : Évolution de la puissance totale installée à Sastra entre 2016 et 2023</h3>
            </div>
            <div className="fullscreen-chart-container">
              <svg className="animated-bar-chart fullscreen" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid meet">
                {/* Flèche de croissance */}
                <defs>
                  <marker id="arrowhead-full" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                    <polygon points="0 0, 10 3, 0 6" fill="#1e3a5f" />
                  </marker>
                </defs>

                {/* Ligne de croissance avec flèche zigzag */}
                <path
                  className="growth-arrow"
                  d={`M 70 ${350 - (yearlyData[0].power / 4000) * 300}
                      L ${70 + 85} ${350 - (yearlyData[1].power / 4000) * 300}
                      L ${70 + 85 * 2} ${350 - (yearlyData[2].power / 4000) * 300}
                      L ${70 + 85 * 3} ${350 - (yearlyData[3].power / 4000) * 300}
                      L ${70 + 85 * 4} ${350 - (yearlyData[4].power / 4000) * 300}
                      L ${70 + 85 * 5} ${350 - (yearlyData[5].power / 4000) * 300}
                      L ${70 + 85 * 6} ${350 - (yearlyData[6].power / 4000) * 300}
                      L 730 ${350 - (yearlyData[yearlyData.length - 1].power / 4000) * 300}`}
                  stroke="#1e3a5f"
                  strokeWidth="4"
                  fill="none"
                  markerEnd="url(#arrowhead-full)"
                />

                {/* Axes */}
                <line x1="50" y1="350" x2="750" y2="350" stroke="#ccc" strokeWidth="2" />
                <line x1="50" y1="50" x2="50" y2="350" stroke="#ccc" strokeWidth="2" />

                {/* Labels Y-axis */}
                <text x="30" y="355" fontSize="14" fill="#666">0</text>
                <text x="15" y="255" fontSize="14" fill="#666">1000</text>
                <text x="15" y="155" fontSize="14" fill="#666">2000</text>
                <text x="15" y="80" fontSize="14" fill="#666">3000</text>
                <text x="15" y="55" fontSize="14" fill="#666">4000</text>
                <text x="5" y="30" fontSize="15" fill="#1e3a5f" fontWeight="bold">kWc</text>

                {/* Barres */}
                {yearlyData.map((data, index) => {
                  const x = 70 + index * 85;
                  const barHeight = (data.power / 4000) * 300;
                  const y = 350 - barHeight;

                  return (
                    <g key={data.year}>
                      {/* Barre principale */}
                      <rect
                        x={x}
                        y={y}
                        width="60"
                        height={barHeight}
                        fill="#1e3a5f"
                        filter="url(#barShadowFull)"
                        rx="4"
                        ry="4"
                      />

                      {/* Valeur au-dessus */}
                      <rect
                        x={x + 5}
                        y={y - 45}
                        width="50"
                        height="28"
                        fill="white"
                        rx="5"
                        opacity="0.95"
                        stroke="#e0e0e0"
                        strokeWidth="1.5"
                      />
                      <text
                        x={x + 30}
                        y={y - 25}
                        fontSize="17"
                        fontWeight="bold"
                        fill="#1e3a5f"
                        textAnchor="middle"
                      >
                        {data.power}
                      </text>

                      {/* Année */}
                      <text
                        x={x + 30}
                        y="370"
                        fontSize="15"
                        fontWeight="bold"
                        fill="#1e3a5f"
                        textAnchor="middle"
                      >
                        {data.year}
                      </text>
                    </g>
                  );
                })}

                {/* Filtres */}
                <defs>
                  <filter id="barShadowFull" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
                    <feOffset dx="2" dy="4" result="offsetblur" />
                    <feComponentTransfer>
                      <feFuncA type="linear" slope="0.5" />
                    </feComponentTransfer>
                    <feMerge>
                      <feMergeNode />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Modal Détail Référence */}
      {selectedReference && (
        <div className="reference-detail-modal" onClick={closeReferenceDetail}>
          <div className="reference-detail-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-detail-btn" onClick={closeReferenceDetail} title="Fermer (Échap)">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="reference-detail-body">
              <div className="detail-logo">
                <img
                  src={`${API_URL.replace('/api', '')}${selectedReference.logo}`}
                  alt={selectedReference.nom}
                />
              </div>
              <div className="detail-info">
                <h2>{selectedReference.nom}</h2>
                <div
                  className="detail-type-badge"
                  style={{backgroundColor: getTypeColor(selectedReference.typeReference)}}
                >
                  {selectedReference.typeReference}
                </div>
                <div className="detail-power">
                  <span className="detail-power-icon">⚡</span>
                  <span className="detail-power-value">{selectedReference.puissance} kWc</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default References;
